<script>
  import SettingsLayout from '../../components/SettingsLayout.svelte';
  import { onMount } from 'svelte';

  let loading = true;
  let saving = false;
  let error = null;
  let success = null;

  let organisation = null;
  let role = null;          // the current user's role
  let members = [];

  let newMemberEmail = '';
  let newMemberRole = 'office_admin';

  // Role catalog (owner is not assignable here)
  const ROLE_LABELS = {
    owner: 'Owner — final approval & payment',
    admin: 'Admin — manage members & settings',
    office_admin: 'Office Administrator — enters & assigns the bill',
    estimator_qs: 'Estimator / QS — checks values & quantities',
    project_manager: 'Project Manager — confirms work completed'
  };
  const ASSIGNABLE_ROLES = ['office_admin', 'estimator_qs', 'project_manager', 'admin'];
  const PHASE_ROLES = ['office_admin', 'estimator_qs', 'project_manager'];

  $: isAdmin = role === 'owner' || role === 'admin';
  $: requiredPhases = organisation?.requiredApprovalPhases || [];

  onMount(load);

  async function load() {
    loading = true; error = null;
    try {
      const res = await fetch('/api/organisations/current');
      const data = await res.json();
      if (res.ok && data.success) {
        organisation = data.organisation;
        role = data.role;
        members = data.members || [];
      } else {
        error = data.error || 'Failed to load organisation';
      }
    } catch (e) {
      error = 'Failed to load organisation. ' + e.message;
    } finally {
      loading = false;
    }
  }

  function flash(msg) { success = msg; setTimeout(() => { success = null; }, 2500); }

  async function renameOrg() {
    saving = true; error = null;
    try {
      const res = await fetch(`/api/organisations/${organisation.orgId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: organisation.name })
      });
      const data = await res.json();
      if (res.ok && data.success) { organisation = data.organisation; flash('✅ Saved'); }
      else error = data.error || 'Failed to save';
    } catch (e) { error = e.message; } finally { saving = false; }
  }

  async function addMember() {
    if (!newMemberEmail.trim()) { error = 'Enter the member\'s email'; return; }
    saving = true; error = null;
    try {
      const res = await fetch(`/api/organisations/${organisation.orgId}/members`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newMemberEmail.trim(), role: newMemberRole })
      });
      const data = await res.json();
      if (res.ok && data.success) { newMemberEmail = ''; flash('✅ Member added'); await load(); }
      else error = data.error || 'Failed to add member';
    } catch (e) { error = e.message; } finally { saving = false; }
  }

  async function changeRole(member, newRole) {
    error = null;
    try {
      const res = await fetch(`/api/organisations/${organisation.orgId}/members/${member.userId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      });
      const data = await res.json();
      if (res.ok && data.success) { flash('✅ Role updated'); await load(); }
      else { error = data.error || 'Failed to update role'; await load(); }
    } catch (e) { error = e.message; }
  }

  async function removeMember(member) {
    if (!confirm(`Remove ${member.username || member.userId} from the organisation?`)) return;
    error = null;
    try {
      const res = await fetch(`/api/organisations/${organisation.orgId}/members/${member.userId}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.success) { flash('✅ Member removed'); await load(); }
      else error = data.error || 'Failed to remove member';
    } catch (e) { error = e.message; }
  }

  async function toggleApproval() {
    await saveConfig({ approvalEnabled: !organisation.approvalEnabled });
  }
  async function togglePhase(phase) {
    const set = new Set(requiredPhases);
    set.has(phase) ? set.delete(phase) : set.add(phase);
    await saveConfig({ requiredApprovalPhases: PHASE_ROLES.filter(p => set.has(p)) });
  }
  async function saveConfig(patch) {
    saving = true; error = null;
    try {
      const res = await fetch(`/api/organisations/${organisation.orgId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch)
      });
      const data = await res.json();
      if (res.ok && data.success) { organisation = data.organisation; flash('✅ Saved'); }
      else error = data.error || 'Failed to save';
    } catch (e) { error = e.message; } finally { saving = false; }
  }
</script>

<SettingsLayout activeTab="organisation" tabTitle="Organisation Roles">
  <p class="text-gray-600 mb-6">Manage your organisation's members and the roles used for bill approvals.</p>

  {#if error}<div class="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">{error}</div>{/if}
  {#if success}<div class="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded">{success}</div>{/if}

  {#if loading}
    <div class="flex items-center py-8"><div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mr-3"></div>Loading…</div>
  {:else if !organisation}
    <!-- No organisation yet: orgs are created by connecting JobTread -->
    <div class="bg-white rounded-lg shadow p-6">
      <h2 class="text-lg font-semibold mb-2">No organisation yet</h2>
      <p class="text-sm text-gray-600 mb-2">Your organisation is created automatically when you connect JobTread. Go to <span class="font-medium">Settings → JobTread</span>, paste your grant key, select your organisation, and check the connection.</p>
      <p class="text-sm text-gray-600">The first person to connect a JobTread organisation becomes its owner and can then invite members and assign roles here. If your organisation is already connected by a colleague, ask them to invite you.</p>
    </div>
  {:else}
    <!-- Org overview -->
    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <div class="flex items-center justify-between mb-1">
        <h2 class="text-lg font-semibold">Organisation details</h2>
        <span class="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-medium">Your role: {ROLE_LABELS[role] || role}</span>
      </div>
      {#if isAdmin}
        <div class="flex gap-2 max-w-md mt-3">
          <input bind:value={organisation.name} class="flex-1 px-3 py-2 border border-gray-300 rounded-md" />
          <button on:click={renameOrg} disabled={saving} class="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:opacity-50">Save name</button>
        </div>
      {:else}
        <p class="text-gray-800 mt-2 font-medium">{organisation.name}</p>
      {/if}
    </div>

    {#if isAdmin}
      <!-- Members -->
      <div class="bg-white rounded-lg shadow p-6 mb-6">
        <h2 class="text-lg font-semibold mb-4">Members &amp; roles</h2>
        <div class="border rounded-md overflow-hidden mb-4">
          <div class="bg-gray-50 px-4 py-2 grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600">
            <div class="col-span-5">User</div><div class="col-span-5">Role</div><div class="col-span-2"></div>
          </div>
          {#each members as m}
            <div class="px-4 py-2 grid grid-cols-12 gap-2 items-center border-t text-sm">
              <div class="col-span-5">{m.username || m.userId}</div>
              <div class="col-span-5">
                {#if m.role === 'owner'}
                  <span class="text-gray-700">{ROLE_LABELS.owner}</span>
                {:else}
                  <select value={m.role} on:change={(e) => changeRole(m, e.currentTarget.value)} class="w-full px-2 py-1 border border-gray-300 rounded">
                    {#each ASSIGNABLE_ROLES as r}<option value={r}>{ROLE_LABELS[r]}</option>{/each}
                  </select>
                {/if}
              </div>
              <div class="col-span-2 text-right">
                {#if m.role !== 'owner'}
                  <button on:click={() => removeMember(m)} class="text-red-600 hover:text-red-800 text-xs">Remove</button>
                {/if}
              </div>
            </div>
          {/each}
        </div>

        <div class="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div class="flex-1">
            <label class="block text-xs font-medium text-gray-700 mb-1">Add member by email</label>
            <input bind:value={newMemberEmail} placeholder="person@company.com" class="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Role</label>
            <select bind:value={newMemberRole} class="px-3 py-2 border border-gray-300 rounded-md">
              {#each ASSIGNABLE_ROLES as r}<option value={r}>{ROLE_LABELS[r]}</option>{/each}
            </select>
          </div>
          <button on:click={addMember} disabled={saving} class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap">Add member</button>
        </div>
        <p class="text-xs text-gray-500 mt-2">The person must already have a Buildzilla account. A user can belong to one organisation.</p>
      </div>

      <!-- Approval phases -->
      <div class="bg-white rounded-lg shadow p-6">
        <h2 class="text-lg font-semibold mb-1">Bill approval phases</h2>
        <p class="text-sm text-gray-600 mb-4">Choose which review phases a bill must pass before the owner gives final approval to push to JobTread/Xero. Leave a phase off if your organisation doesn't use that role.</p>
        <label class="flex items-center gap-2 mb-4 opacity-60 cursor-not-allowed">
          <input type="checkbox" checked={organisation.approvalEnabled} on:change={toggleApproval} disabled class="h-4 w-4" />
          <span class="text-sm font-medium text-gray-400">Require multi-phase approval before a bill is processed - Coming soon</span>
        </label>
        <div class="space-y-2 {organisation.approvalEnabled ? '' : 'opacity-50 pointer-events-none'}">
          {#each PHASE_ROLES as p}
            <label class="flex items-center gap-2">
              <input type="checkbox" checked={requiredPhases.includes(p)} on:change={() => togglePhase(p)} class="h-4 w-4" />
              <span class="text-sm">{ROLE_LABELS[p]}</span>
            </label>
          {/each}
          <p class="text-xs text-gray-500 pt-1">Owner approval is always the final step.</p>
        </div>
      </div>
    {/if}
  {/if}
</SettingsLayout>
